/* ─────────────────────────────────────────────────────────────────────────
   "How to reach" corridor config — one entry per SEO landing page.
   Used by pages/HowToReach.jsx (detail) and pages/HowToReachIndex.jsx (hub).
   Adding a corridor here automatically creates its page + index card.
   ───────────────────────────────────────────────────────────────────────── */
export const CORRIDORS = {
    'mumbai-to-munnar': {
        from: { name: 'Mumbai', lat: 18.9582347, lng: 72.8319514, type: 'google' },
        to: { name: 'Munnar', lat: 10.0889333, lng: 77.0595248, type: 'google' },
        title: 'How to Reach Munnar from Mumbai (2026): Flight, Train & Cab Options',
        metaDescription:
            'The complete Mumbai to Munnar travel guide — flights to Kochi, direct trains to Ernakulam, Pollachi & Madurai, cab distances, journey times and live fares.',
        quickAnswer:
            'The fastest way to reach Munnar from Mumbai is a ~2 hour flight from Mumbai (BOM) to Kochi (COK), followed by a 106.7 km cab ride (about 3–3.5 hours) up the ghats to Munnar. The cheapest way is a direct train from Lokmanya Tilak Terminus to Ernakulam Jn, then a 124 km cab.',
        intro: [
            "Munnar, Kerala's most loved hill station, has no airport and no railway station of its own — every trip ends with a scenic road climb through tea estates. That makes choosing the right gateway city the most important decision of your journey from Mumbai.",
            'You have two practical gateways: fly into Kochi and drive up, or take a direct overnight train to Ernakulam (Kochi), Pollachi or Madurai and complete the trip by cab. Below is exactly how each option works, with real distances and typical journey times.',
        ],
        gateways: [
            { name: 'Kochi Airport (COK)', type: 'Airport', distanceKm: 106.7, cabTime: '3–3.5 hrs' },
            { name: 'Pollachi Jn (POY)', type: 'Railway Station', distanceKm: 115.3, cabTime: '3–3.5 hrs' },
            { name: 'Ernakulam Jn (ERS)', type: 'Railway Station', distanceKm: 124, cabTime: '3.5–4 hrs' },
            { name: 'Madurai Jn (MDU)', type: 'Railway Station', distanceKm: 158.6, cabTime: '4–4.5 hrs' },
        ],
        options: [
            {
                id: 'flight-kochi',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Mumbai → Kochi, then cab to Munnar',
                duration: '≈ 5.5–6 hrs door to door',
                fareNote: 'Flights from ~₹5,700 · Cab ~₹3,500–5,000',
                modesParam: 'Flight',
                body:
                    'Multiple daily non-stop flights connect Mumbai (BOM) and Navi Mumbai (NMI) to Kochi (COK) in about 2 hours — IndiGo, Akasa Air and Air India Express all fly this route. From Kochi airport, Munnar is a 106.7 km drive (3–3.5 hours) through Neriamangalam and Adimali, one of the prettiest airport transfers in India. Land by early afternoon and you will reach Munnar before sunset.',
            },
            {
                id: 'train-ernakulam',
                icon: 'train',
                badge: 'Cheapest',
                title: 'Direct train Mumbai → Ernakulam Jn, then cab',
                duration: '≈ 26–29 hrs door to door',
                fareNote: 'Trains from ~₹1,900 (SL/3A) · Cab ~₹3,500–5,500',
                modesParam: 'Train',
                body:
                    'Direct daily trains run from Lokmanya Tilak Terminus (LTT) to Ernakulam Jn (ERS) — the 16345 Netravati Express (24h 50m) and the faster 12223 LTT–ERS Duronto (22h 5m) are the popular picks. From Ernakulam, Munnar is a 124 km cab ride (3.5–4 hours). Board an evening departure, sleep through the Konkan coast, and drive up the next afternoon.',
            },
            {
                id: 'train-pollachi',
                icon: 'train',
                badge: 'Shortest cab from a station',
                title: 'Train to Pollachi Jn, then cab via Marayoor',
                duration: '≈ 27–30 hrs door to door',
                fareNote: 'Cab ~₹3,200–4,800',
                modesParam: 'Train',
                body:
                    'Pollachi Jn (Tamil Nadu side) is the closest railway station to Munnar by road distance — 115.3 km, approached via the quieter Udumalpet–Marayoor route past Chinnar Wildlife Sanctuary and the sandalwood forests. Connectivity from Mumbai usually involves one change, so it suits travellers who prefer the offbeat eastern approach.',
            },
            {
                id: 'train-madurai',
                icon: 'train',
                badge: 'Temple-circuit combo',
                title: 'Train to Madurai Jn, then cab via Theni',
                duration: '≈ 30+ hrs door to door',
                fareNote: 'Cab ~₹4,000–6,000',
                modesParam: 'Train',
                body:
                    'Madurai Jn is 158.6 km from Munnar — the longest cab leg of the four, but it lets you pair the Meenakshi Temple with your hill-station trip. The drive climbs through Theni and Bodinayakkanur into the Western Ghats. Pick this if Madurai itself is on your itinerary.',
            },
        ],
        steps: [
            'Book a morning Mumbai (BOM) → Kochi (COK) flight — about 2 hours in the air.',
            'Pre-book a cab from Kochi airport to Munnar (106.7 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive up via Neriamangalam and Adimali — around 3 to 3.5 hours with a tea break at Valara or Cheeyappara waterfalls.',
            'Reach Munnar by evening. Most hotels are around Old Munnar or along the Chinnakanal road.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Munnar?',
                a: 'Kochi International Airport (COK) is the nearest airport to Munnar, 106.7 km away — roughly a 3 to 3.5 hour drive. Madurai and Coimbatore airports are alternatives but involve longer road journeys.',
            },
            {
                q: 'Which is the nearest railway station to Munnar?',
                a: 'By road distance, Pollachi Jn (115.3 km) is the closest station, followed by Ernakulam Jn (124 km) and Madurai Jn (158.6 km). Ernakulam has the best direct connectivity from Mumbai.',
            },
            {
                q: 'Is there a direct train from Mumbai to Munnar?',
                a: 'No — Munnar has no railway station. The practical rail route is a direct train from Lokmanya Tilak Terminus to Ernakulam Jn, such as the 16345 Netravati Express or 12223 Duronto, followed by a 124 km cab to Munnar.',
            },
            {
                q: 'How much does a cab from Kochi airport to Munnar cost?',
                a: 'A one-way drop typically costs around ₹3,500–5,000 depending on the car type (hatchback, sedan or SUV). RouteChef shows live cab fares alongside your flight or train so you can budget the full door-to-door trip.',
            },
            {
                q: 'What is the cheapest way to reach Munnar from Mumbai?',
                a: 'A sleeper or 3A ticket on a direct LTT → Ernakulam train (from about ₹1,900) plus a shared or budget cab from Ernakulam is the cheapest practical combination, bringing the total under ₹4,000 per person for most dates.',
            },
        ],
    },
    'mumbai-to-varkala': {
        from: { name: 'Mumbai', lat: 18.9582347, lng: 72.8319514, type: 'google' },
        to: { name: 'Varkala', lat: 8.7378685, lng: 76.7163359, type: 'google' },
        title: 'How to Reach Varkala from Mumbai (2026): Flight, Direct Train & Cab Options',
        metaDescription:
            'The complete Mumbai to Varkala travel guide — flights to Trivandrum, the direct Netravati Express, trains to Kollam Jn, cab distances, journey times and live fares.',
        quickAnswer:
            'The fastest way to reach Varkala from Mumbai is a ~2 hour flight to Trivandrum (TRV), then a 41.4 km cab (about 1–1.5 hours) to Varkala cliff. Uniquely, Varkala also has its own railway station — the 16345 Netravati Express runs direct from Lokmanya Tilak Terminus to Varkala Sivagiri in about 29 hours.',
        intro: [
            "Varkala sits on Kerala's south coast, famous for its dramatic red cliff over the Arabian Sea. Unlike most beach and hill escapes, it actually has its own railway station — Varkala Sivagiri, barely 10 minutes from the cliff — so a genuinely direct train from Mumbai exists. The trade-off is time: the rail journey takes over a day, while flying to Trivandrum gets you on the cliff by lunch.",
            'Your three practical patterns: fly to Trivandrum and cab it (fastest), take the direct Netravati Express (zero transfers), or take a faster/cheaper train to Kollam Jn and cover the last 33.6 km by cab. There is also a clever mixed option via Kochi when Trivandrum fares spike.',
        ],
        gateways: [
            { name: 'Varkala Sivagiri (VAK)', type: 'Railway Station', distanceKm: 3, cabTime: '10 min' },
            { name: 'Kollam Jn (QLN)', type: 'Railway Station', distanceKm: 33.6, cabTime: '45–60 min' },
            { name: 'Trivandrum Airport (TRV)', type: 'Airport', distanceKm: 41.4, cabTime: '1–1.5 hrs' },
        ],
        options: [
            {
                id: 'flight-trivandrum',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Mumbai → Trivandrum, then cab to Varkala',
                duration: '≈ 4–4.5 hrs door to door',
                fareNote: 'Flights from ~₹6,000 · Cab ~₹1,200–1,800',
                modesParam: 'Flight',
                body:
                    'Non-stop flights from Mumbai (BOM) and Navi Mumbai (NMI) reach Trivandrum (TRV) in just over 2 hours. From the airport, Varkala is a 41.4 km drive up the coast — 1 to 1.5 hours depending on Kazhakkoottam traffic. Take a morning flight and you are watching the sunset from the cliff the same evening.',
            },
            {
                id: 'train-direct-varkala',
                icon: 'train',
                badge: 'Direct · No cab needed',
                title: 'Direct train Mumbai → Varkala Sivagiri',
                duration: '≈ 29 hrs, zero transfers',
                fareNote: 'From ~₹700 (SL) · ~₹1,925 (3A)',
                modesParam: 'Train',
                body:
                    'The 16345 Netravati Express departs Lokmanya Tilak Terminus at 11:40 and pulls into Varkala Sivagiri at 16:51 the next day — a 29 hour coastal run down the Konkan with no changes and no cab at the end. The station is about 3 km from the cliff; autos do the last hop in 10 minutes. The slowest option on paper, but the simplest by far.',
            },
            {
                id: 'train-kollam',
                icon: 'train',
                badge: 'Cheapest',
                title: 'Train to Kollam Jn, then a 33.6 km cab',
                duration: '≈ 27–29 hrs door to door',
                fareNote: 'Trains from ~₹1,305 (3A Garib Rath) · Cab ~₹1,000–1,500',
                modesParam: 'Train',
                body:
                    'Kollam Jn is a bigger junction with more Mumbai trains and often better availability: the 12201 KCVL Garib Rath (LTT 16:45 → Kollam 18:12 next day, about 25.5 hours) is both the cheapest and quickest rail option, and the 20910 Kochuveli Express and 16335 Nagercoil Express run from Vasai Road. From Kollam, Varkala is a 33.6 km cab ride of 45–60 minutes along the coast.',
            },
            {
                id: 'flight-kochi-train',
                icon: 'flight',
                badge: 'Smart mixed option',
                title: 'Fly Mumbai → Kochi, then train down to Kollam',
                duration: '≈ 9–10 hrs door to door',
                fareNote: 'Flights from ~₹5,500 · Train ~₹150 (SL)',
                modesParam: '',
                body:
                    'When Trivandrum fares spike, Kochi (COK) flights are often noticeably cheaper. Land at Kochi, transfer 27 km to Ernakulam Town, and ride a passenger train like the 16792 Palaruvi Express down to Kollam (about 4.5 hours, tickets from ₹150), finishing with the short cab to Varkala. More moving parts, but frequently the best fare-to-time balance — RouteChef stitches this combination automatically.',
            },
        ],
        steps: [
            'Book a morning Mumbai (BOM/NMI) → Trivandrum (TRV) flight — about 2 hours in the air.',
            'Pre-book a cab from Trivandrum airport to Varkala (41.4 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive up the coastal highway past Kazhakkoottam and Attingal — 1 to 1.5 hours.',
            'Reach Varkala cliff by early afternoon. Most stays cluster along North Cliff and Odayam beach.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Varkala?',
                a: 'Trivandrum International Airport (TRV) is the nearest airport, 41.4 km from Varkala — roughly a 1 to 1.5 hour drive. Kochi airport is an alternative when fares are cheaper, combined with a train down to Kollam or Varkala.',
            },
            {
                q: 'Which is the nearest railway station to Varkala?',
                a: 'Varkala has its own station — Varkala Sivagiri (VAK), about 3 km from the cliff. Kollam Jn (33.6 km) is the nearest major junction, with more trains from Mumbai and better seat availability.',
            },
            {
                q: 'Is there a direct train from Mumbai to Varkala?',
                a: 'Yes — the 16345 Netravati Express runs daily from Lokmanya Tilak Terminus to Varkala Sivagiri, departing 11:40 and arriving 16:51 the next day (about 29 hours), with no transfers needed.',
            },
            {
                q: 'How much does a cab from Trivandrum airport to Varkala cost?',
                a: 'A one-way drop for the 41.4 km ride typically costs around ₹1,200–1,800 depending on the car type. RouteChef shows live cab fares alongside your flight so you can budget the full door-to-door trip.',
            },
            {
                q: 'What is the cheapest way to reach Varkala from Mumbai?',
                a: 'A 3A berth on the 12201 KCVL Garib Rath to Kollam Jn (from about ₹1,305) plus a short cab, or a sleeper berth on the direct Netravati Express (from about ₹700) with no cab at all. Both keep the total under ₹2,000 for most dates.',
            },
        ],
    },
    'pune-to-varkala': {
        from: { name: 'Pune', lat: 18.5246091, lng: 73.8786239, type: 'google' },
        to: { name: 'Varkala', lat: 8.7378685, lng: 76.7163359, type: 'google' },
        title: 'How to Reach Varkala from Pune (2026): Flight + Train Combo, Direct Train & Cab',
        metaDescription:
            'The complete Pune to Varkala travel guide — the fast flight-to-Kochi + train combo, the direct Pune–Cape Express, trains to Ernakulam & Kollam, cab distances and live fares.',
        quickAnswer:
            'The fastest way to reach Varkala from Pune is an early flight to Kochi (~2 hours), a connecting train down to Kollam and a short cab — about 8.5 hours door to door. Pune also has a direct train: the 16381 Pune–Cape Express runs all the way to Varkala Sivagiri station in about 32 hours, with no cab needed at the end.',
        intro: [
            'Varkala\u2019s cliff-top beaches are a long way from Pune — around 1,300 km — but the journey is friendlier than the distance suggests. Pune has something even Mumbai lacks for this corridor: a direct train that stops at Varkala Sivagiri itself, plus a clever flight-and-train combination through Kochi that gets you from Pune to the cliff in a single morning.',
            'There is no non-stop Pune–Trivandrum flight, which changes the usual playbook: flying into Kochi and riding the rails down the coast beats connecting flights on both time and fare for most dates. Here is how every option works.',
        ],
        gateways: [
            { name: 'Varkala Sivagiri (VAK)', type: 'Railway Station', distanceKm: 3, cabTime: '10 min' },
            { name: 'Kollam Jn (QLN)', type: 'Railway Station', distanceKm: 33.6, cabTime: '45–60 min' },
            { name: 'Trivandrum Airport (TRV)', type: 'Airport', distanceKm: 41.4, cabTime: '1–1.5 hrs' },
        ],
        options: [
            {
                id: 'flight-kochi-train',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Pune → Kochi, then train down to Kollam',
                duration: '≈ 8.5–9 hrs door to door',
                fareNote: 'Total from ~₹7,000 (flight + train + cab)',
                modesParam: '',
                body:
                    'Early flights connect Pune (PNQ) to Kochi (COK) in under 2 hours — the 01:35 Air India Express 2739 lands at 03:30, and IndiGo\u2019s 03:05 departure follows it. A 27 km cab brings you to Ernakulam Town station for the 07:15 Kanyakumari Express, which reaches Kollam at 10:10 (3A from ₹520). One short 33.6 km cab later, you are on Varkala cliff by lunchtime. RouteChef stitches this exact flight + train + cab combination in one search.',
            },
            {
                id: 'train-direct-varkala',
                icon: 'train',
                badge: 'Direct · No cab needed',
                title: 'Direct train: 16381 Pune–Cape Express to Varkala Sivagiri',
                duration: '≈ 32 hrs, zero transfers',
                fareNote: 'From ~₹700 (SL) · ~₹1,960 (3A)',
                modesParam: 'Train',
                body:
                    'The 16381 Pune–Kanyakumari (Cape) Express leaves Pune Jn at 23:45 and rolls into Varkala Sivagiri at 07:40 on the second morning — about 32 hours, but with a berth the whole way, no transfers, and the station just 3 km from the cliff. For travellers who prefer one booking and zero logistics, this is the simplest route to Varkala from anywhere in Maharashtra.',
            },
            {
                id: 'train-ernakulam-combo',
                icon: 'train',
                badge: 'Budget rail combo',
                title: 'Overnight train to Ernakulam, connect to Kollam',
                duration: '≈ 33–34 hrs door to door',
                fareNote: 'Trains ~₹1,680 + ₹520 · Cab ~₹1,000–1,500',
                modesParam: 'Train',
                body:
                    'The 11097 Poorna Express departs Pune Jn at 22:50 and reaches Ernakulam Town at 02:20 two nights later. A quick 3.6 km hop to Ernakulam Jn connects you to the 04:15 Kochuveli Express, arriving Kollam at 07:22, with the usual 33.6 km cab to finish. More changes than the direct train, but it opens up additional departure days and often better berth availability.',
            },
            {
                id: 'flight-trivandrum',
                icon: 'flight',
                badge: 'When timing beats fare',
                title: 'Fly Pune → Trivandrum (via Bengaluru), then cab',
                duration: '≈ 7–8 hrs door to door',
                fareNote: 'Connecting flights from ~₹12,000 · Cab ~₹1,200–1,800',
                modesParam: 'Flight',
                body:
                    'There is no non-stop Pune–Trivandrum service, so this route means a connection — typically IndiGo via Bengaluru, about 5 hours 10 minutes in total, landing at TRV by midday. From the airport, Varkala is a 41.4 km cab up the coast. It costs notably more than the Kochi combination, but suits travellers who want a single-mode air journey with daytime timings.',
            },
        ],
        steps: [
            'Book the early Pune (PNQ) → Kochi (COK) flight — the 01:35 or 03:05 departure, about 2 hours in the air.',
            'Take a pre-booked cab from Kochi airport to Ernakulam Town station (27.2 km, ~48 minutes).',
            'Board the 07:15 Kanyakumari Express to Kollam Jn — 3 hours along the backwater country (3A from ₹520).',
            'Finish with the 33.6 km cab from Kollam to Varkala — on the cliff by lunchtime.',
        ],
        faqs: [
            {
                q: 'Is there a direct train from Pune to Varkala?',
                a: 'Yes — the 16381 Pune–Kanyakumari (Cape) Express runs direct from Pune Jn to Varkala Sivagiri, departing 23:45 and arriving 07:40 on the second morning, roughly 32 hours with no transfers.',
            },
            {
                q: 'Is there a direct flight from Pune to Trivandrum?',
                a: 'No — Pune–Trivandrum requires a connection, usually via Bengaluru (about 5 hours in total). That is why flying into Kochi and taking a train down to Kollam is usually faster and cheaper from Pune.',
            },
            {
                q: 'What is the fastest way from Pune to Varkala?',
                a: 'The early flight to Kochi, the 07:15 Kanyakumari Express to Kollam, and a 33.6 km cab — around 8.5 hours door to door and typically about ₹7,000 in total.',
            },
            {
                q: 'Which is the nearest airport and railway station to Varkala?',
                a: 'Trivandrum airport (TRV) is the nearest airport at 41.4 km. Varkala has its own station — Varkala Sivagiri, 3 km from the cliff — while Kollam Jn (33.6 km) is the nearest major junction with the widest train connectivity.',
            },
            {
                q: 'What is the cheapest way to reach Varkala from Pune?',
                a: 'A sleeper berth on the direct 16381 Pune–Cape Express (from about ₹700, no cab needed) is the cheapest practical option; 3A costs about ₹1,960. Rail combos via Ernakulam land in a similar range.',
            },
        ],
    },
    'mumbai-to-rishikesh': {
        from: { name: 'Mumbai', lat: 18.9582347, lng: 72.8319514, type: 'google' },
        to: { name: 'Rishikesh', lat: 30.0869281, lng: 78.2676116, type: 'google' },
        title: 'How to Reach Rishikesh from Mumbai (2026): Direct Flight, Trains & Cab Options',
        metaDescription:
            'The complete Mumbai to Rishikesh travel guide — direct flights to Dehradun, the Bandra–Haridwar Express, Vande Bharat & Janshatabdi combos via Delhi, cab distances and live fares.',
        quickAnswer:
            'The fastest way to reach Rishikesh from Mumbai is a direct ~2.5 hour flight to Dehradun (Jolly Grant airport), followed by a short 15.5 km cab — under 4 hours door to door. The cheapest is the direct 19019 Bandra Terminus–Haridwar Express (about 31.5 hours), with a 25.1 km cab from Haridwar Jn to finish.',
        intro: [
            'Rishikesh — the yoga capital on the Ganga — has no airport and only limited train service of its own, so every journey from Mumbai routes through one of two gateways: Dehradun\u2019s Jolly Grant airport, just 15.5 km away, or Haridwar Jn, 25.1 km down the river.',
            'The good news is that both gateways are unusually well connected from Mumbai: direct flights reach Jolly Grant in about two and a half hours, a direct overnight-plus train runs from Bandra Terminus to Haridwar, and if you fly into Delhi instead, the Vande Bharat and Dehradun Janshatabdi both make the Haridwar hop in under 4 hours the same afternoon.',
        ],
        gateways: [
            { name: 'Jolly Grant Airport, Dehradun (DED)', type: 'Airport', distanceKm: 15.5, cabTime: '30–40 min' },
            { name: 'Haridwar Jn (HW)', type: 'Railway Station', distanceKm: 25.1, cabTime: '35–45 min' },
            { name: 'Yog Nagari Rishikesh (YNRK)', type: 'Railway Station', distanceKm: 2, cabTime: '5–10 min' },
        ],
        options: [
            {
                id: 'flight-dehradun',
                icon: 'flight',
                badge: 'Fastest · Recommended',
                title: 'Fly Mumbai → Dehradun direct, then a short cab',
                duration: '≈ 3.5–4 hrs door to door',
                fareNote: 'Flights from ~₹6,700 · Cab ~₹700–1,200',
                modesParam: 'Flight',
                body:
                    'IndiGo flies non-stop from Navi Mumbai (NMI) to Dehradun\u2019s Jolly Grant airport in about 2 hours 20 minutes — the 15:15 departure lands at 17:35. From there Rishikesh is only 15.5 km, one of the shortest airport transfers to any Indian getaway: 30–40 minutes and you are at Tapovan or Ram Jhula. Leave Mumbai after lunch, catch the evening Ganga aarti.',
            },
            {
                id: 'flight-delhi-train',
                icon: 'flight',
                badge: 'Most departures · Same-day',
                title: 'Fly to Delhi, then Vande Bharat or Janshatabdi to Haridwar',
                duration: '≈ 9–10 hrs door to door',
                fareNote: 'Flights from ~₹5,000 · Train CC ₹495–985 · Cab ~₹900–1,400',
                modesParam: '',
                body:
                    'Mumbai–Delhi has flights nearly every hour, which makes this the most flexible routing. Land by early afternoon, transfer to the station, and take either the 22457 Vande Bharat Express from Anand Vihar (17:50 → Haridwar 21:11, CC ₹985) or the 12055 Dehradun Janshatabdi from New Delhi (15:20 → Haridwar 19:27, CC ₹495). A 25.1 km cab completes the trip the same evening. RouteChef stitches the flight, the station transfer and the train in one search.',
            },
            {
                id: 'train-direct-haridwar',
                icon: 'train',
                badge: 'Direct · Cheapest',
                title: 'Direct train: 19019 Bandra Terminus–Haridwar Express',
                duration: '≈ 32 hrs door to door',
                fareNote: 'From ~₹700 (SL) · ~₹1,810 (3A) · Cab ~₹900–1,400',
                modesParam: 'Train',
                body:
                    'The 19019 BDTS–Haridwar Express leaves Bandra Terminus at 00:20 and arrives Haridwar Jn at 07:50 the next morning — about 31.5 hours with a berth the whole way. From Haridwar it is a 38-minute, 25.1 km drive along the Ganga to Rishikesh. Slow, but it is the only single-ticket option from Mumbai and comfortably the cheapest.',
            },
        ],
        steps: [
            'Book the afternoon Navi Mumbai (NMI) → Dehradun (DED) non-stop — about 2 hours 20 minutes in the air.',
            'Pre-book a cab from Jolly Grant airport to Rishikesh (15.5 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive 30–40 minutes past Raiwala into Rishikesh — Tapovan and Laxman Jhula are slightly beyond the town centre.',
            'Arrive before sunset — in time for the evening Ganga aarti at Triveni Ghat or Parmarth Niketan.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Rishikesh?',
                a: 'Jolly Grant Airport, Dehradun (DED) is the nearest airport — just 15.5 km from Rishikesh, a 30–40 minute drive. It has direct flights from Mumbai, Delhi and other metros.',
            },
            {
                q: 'Which is the nearest railway station to Rishikesh?',
                a: 'Rishikesh has its own station — Yog Nagari Rishikesh (YNRK), about 2 km from town — but long-distance service there is limited. Haridwar Jn, 25.1 km away, is the practical rail gateway with far more trains, including direct service from Mumbai.',
            },
            {
                q: 'Is there a direct train from Mumbai to Rishikesh?',
                a: 'Not to Rishikesh itself, but the 19019 Bandra Terminus–Haridwar Express runs direct to Haridwar Jn (00:20 → 07:50 next day, about 31.5 hours), leaving only a 25.1 km cab ride to Rishikesh.',
            },
            {
                q: 'What is the fastest way from Mumbai to Rishikesh?',
                a: 'The direct Navi Mumbai → Dehradun flight (about 2.5 hours) plus the 15.5 km cab — under 4 hours door to door. Flying to Delhi and catching the Vande Bharat or Janshatabdi to Haridwar is the next-best, arriving the same evening.',
            },
            {
                q: 'How much does a cab from Dehradun airport or Haridwar to Rishikesh cost?',
                a: 'From Jolly Grant airport (15.5 km) expect roughly ₹700–1,200 one way; from Haridwar Jn (25.1 km) roughly ₹900–1,400 depending on the car type. RouteChef shows live cab fares alongside your flight or train.',
            },
        ],
    },
    'bangalore-to-coorg': {
        from: { name: 'Bangalore', lat: 12.9767936, lng: 77.590082, type: 'google' },
        to: { name: 'Coorg', lat: 12.4207575, lng: 75.7397482, type: 'google' },
        title: 'How to Reach Coorg from Bangalore (2026): Train, Flight & Cab Options',
        metaDescription:
            'The complete Bangalore to Coorg travel guide — trains to Subrahmanya Road & Mysuru, flights to Kannur, cab distances, journey times and live fares for every combination.',
        quickAnswer:
            'Coorg (Madikeri) has no airport and no railway station, so every route ends with a scenic drive. The quickest combination from Bangalore is a 1.5 hour flight to Kannur followed by an 89.4 km cab; by rail, take a train to Subrahmanya Road (85.7 km cab) or Mysuru Jn (117.2 km cab via Kushalnagar). Many travellers simply drive the ~260 km directly in 5.5–6 hours.',
        intro: [
            'Coorg is Karnataka\u2019s coffee country — misty ridges around Madikeri with no airport and no rail line of its own. From Bangalore that leaves a genuine choice: drive the whole way, or shorten the road leg by taking a train or flight to one of three gateways that approach the hills from completely different directions.',
            'Subrahmanya Road station brings you in from the south past Kukke Subrahmanya, Mysuru opens the classic Kushalnagar route past Bylakuppe\u2019s golden monastery, and Kannur airport climbs in from the Kerala side. Here is how each plays out with real times and fares.',
        ],
        gateways: [
            { name: 'Subrahmanya Road (SBHR)', type: 'Railway Station', distanceKm: 85.7, cabTime: '2–2.5 hrs' },
            { name: 'Kannur Airport (CNN)', type: 'Airport', distanceKm: 89.4, cabTime: '2–2.5 hrs' },
            { name: 'Mysuru Jn (MYS)', type: 'Railway Station', distanceKm: 117.2, cabTime: '~3 hrs' },
        ],
        options: [
            {
                id: 'train-subrahmanya',
                icon: 'train',
                badge: 'Best rail combo',
                title: 'Morning train to Subrahmanya Road, then cab up the ghats',
                duration: '≈ 10 hrs door to door',
                fareNote: 'Train from ~₹555 (3E) · Cab ~₹2,500–3,500',
                modesParam: 'Train',
                body:
                    'The 16515 Yesvantpur–Karwar Express departs YPR at 07:00 and reaches Subrahmanya Road at 14:20. From there Coorg is an 85.7 km, roughly 2 hour drive — the shortest cab of any rail gateway — winding up through Sullia with an optional stop at the Kukke Subrahmanya temple. You are in Madikeri by early evening on a ₹555 train ticket.',
            },
            {
                id: 'flight-kannur',
                icon: 'flight',
                badge: 'Quickest',
                title: 'Fly Bangalore → Kannur, then cab from the Kerala side',
                duration: '≈ 5–5.5 hrs door to door',
                fareNote: 'Flights from ~₹6,200 · Cab ~₹2,800–4,000',
                modesParam: 'Flight',
                body:
                    'IndiGo\u2019s evening hop from Bangalore to Kannur takes just 1 hour 25 minutes, and Coorg is an 89.4 km drive from the airport, climbing through Iritty and Virajpet on the quieter western approach. Door to door it edges out the direct drive on time while skipping Bangalore\u2019s exit traffic entirely — worth it when Mysuru-road congestion is at its weekend worst.',
            },
            {
                id: 'train-mysuru',
                icon: 'train',
                badge: 'Most frequent · Classic route',
                title: 'Train to Mysuru, then the Kushalnagar road',
                duration: '≈ 7–8 hrs door to door',
                fareNote: 'Trains from ~₹270 (CC) · Cab ~₹3,000–4,500',
                modesParam: 'Train',
                body:
                    'Bangalore–Mysuru has trains nearly every hour — the 16551 MAS–AP Express does it in 3 hours 25 minutes with chair car seats from ₹270, and Shatabdi or Vande Bharat services are faster still. From Mysuru, the 117.2 km drive to Coorg runs via Kushalnagar, passing Bylakuppe\u2019s Namdroling Monastery and the Dubare elephant camp — many travellers treat the cab leg as the first sightseeing day. Pick a morning departure so the ~3 hour drive finishes in daylight.',
            },
        ],
        steps: [
            'Book the 07:00 Yesvantpur → Subrahmanya Road train (16515 Karwar Express) — 7 hours 20 minutes through the countryside.',
            'Pre-book a cab from Subrahmanya Road for the 85.7 km climb to Madikeri. RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive up via Sullia — about 2 hours, with an optional detour to the Kukke Subrahmanya temple near the station.',
            'Reach Madikeri by early evening; most stays and estates are around Madikeri town, Kakkabe and Suntikoppa.',
        ],
        faqs: [
            {
                q: 'Which is the nearest airport to Coorg?',
                a: 'Kannur International Airport (CNN) is the closest by road at 89.4 km — about a 2 to 2.5 hour drive on the Kerala-side approach. Mysuru and Mangaluru airports are alternatives, and Bangalore itself works for those combining the flight with the classic road trip.',
            },
            {
                q: 'Which is the nearest railway station to Coorg?',
                a: 'Subrahmanya Road (SBHR) at 85.7 km is the closest, with a roughly 2 hour cab to Madikeri. Mysuru Jn (117.2 km) is farther but has far more frequent trains from Bangalore and the popular Kushalnagar route.',
            },
            {
                q: 'Is there a direct train or flight to Coorg?',
                a: 'No — Coorg has neither an airport nor a railway station. Every route combines a train or flight to a gateway (Subrahmanya Road, Mysuru or Kannur) with a 2–3 hour cab into the hills.',
            },
            {
                q: 'Is it better to drive directly from Bangalore to Coorg?',
                a: 'The direct drive is about 260 km and takes 5.5–6 hours via Mysuru and Kushalnagar. It is the most flexible option, but the train-plus-cab combinations avoid Bangalore\u2019s exit traffic and cost far less, and the Kannur flight beats the drive on time.',
            },
            {
                q: 'How much does the cab to Coorg cost from each gateway?',
                a: 'Expect roughly ₹2,500–3,500 from Subrahmanya Road (85.7 km), ₹2,800–4,000 from Kannur airport (89.4 km) and ₹3,000–4,500 from Mysuru (117.2 km), depending on car type. RouteChef shows live cab fares alongside your train or flight.',
            },
        ],
    },
    'bangalore-to-varkala': {
        from: { name: 'Bangalore', lat: 12.9767936, lng: 77.590082, type: 'google' },
        to: { name: 'Varkala', lat: 8.7378685, lng: 76.7163359, type: 'google' },
        title: 'How to Reach Varkala from Bangalore (2026): Direct Trains, Flights & Smart Combos',
        metaDescription:
            'The complete Bangalore to Varkala travel guide — direct overnight trains, flights to Trivandrum & Kochi, the Vande Bharat + Ernad Express same-day rail combo, and live fares.',
        quickAnswer:
            'The simplest fast way to reach Varkala from Bangalore is a morning flight to Trivandrum (1 hour 20 minutes) and a 41.4 km cab — on the cliff by lunch. The clever alternative: fly to Kochi and board the Ernad Express, which runs directly to Varkala Sivagiri station by evening. Overnight, the direct 16526 Kanyakumari Express does Bangalore Cantt → Varkala in about 14 hours with no cab needed.',
        intro: [
            'Bangalore is the best-connected origin for Varkala in the country: direct overnight trains to Varkala Sivagiri itself, quick sub-90-minute flights to both Trivandrum and Kochi, and even a same-day all-rail route using the morning Vande Bharat. The question is not whether you can get there — it is which trade-off of time, fare and sleep suits you.',
            'A quirk worth knowing: from Kochi, the Ernad Express stops at Varkala Sivagiri directly, so the flight-plus-train combination ends 10 minutes from the cliff with no long cab leg at all. Here is every option with real times and fares.',
        ],
        gateways: [
            { name: 'Varkala Sivagiri (VAK)', type: 'Railway Station', distanceKm: 3, cabTime: '10 min' },
            { name: 'Kollam Jn (QLN)', type: 'Railway Station', distanceKm: 33.6, cabTime: '45–60 min' },
            { name: 'Trivandrum Airport (TRV)', type: 'Airport', distanceKm: 41.4, cabTime: '1–1.5 hrs' },
        ],
        options: [
            {
                id: 'flight-trivandrum',
                icon: 'flight',
                badge: 'Quickest · By lunch',
                title: 'Fly Bangalore → Trivandrum, then cab to the cliff',
                duration: '≈ 4 hrs door to door',
                fareNote: 'Flights from ~₹4,900 · Cab ~₹1,200–1,800',
                modesParam: 'Flight',
                body:
                    'IndiGo\u2019s 07:20 departure reaches Trivandrum at 08:40 — just 1 hour 20 minutes in the air — with more services through the morning. From TRV, Varkala is a 41.4 km coastal drive of about an hour and a quarter. Leave Bangalore at breakfast, swim before lunch.',
            },
            {
                id: 'flight-kochi-ernad',
                icon: 'flight',
                badge: 'Smart combo · Train ends at Varkala',
                title: 'Fly to Kochi, then the Ernad Express straight to Varkala',
                duration: '≈ 7 hrs door to door',
                fareNote: 'Flights from ~₹4,300 · Train CC ₹310 · No destination cab',
                modesParam: '',
                body:
                    'Kochi flights are often the cheapest out of Bangalore — AllianceAir\u2019s 07:05 and IndiGo\u2019s midday 12:55 both land in under 90 minutes. A 29.6 km cab brings you to Ernakulam Jn for the 16:20 Ernad Express, which runs down the coast and stops at Varkala Sivagiri itself at 19:57 — chair car ₹310, and the station is 10 minutes from the cliff. Faster morning connections via the 11:43 Vande Bharat to Kollam also exist; RouteChef stitches whichever lands best for your date.',
            },
            {
                id: 'train-direct-overnight',
                icon: 'train',
                badge: 'Direct · Overnight · No cab',
                title: 'Direct overnight train to Varkala Sivagiri',
                duration: '≈ 14–14.5 hrs, zero transfers',
                fareNote: 'From ~₹1,180 (3A) · departs 20:22, arrive 10:37',
                modesParam: 'Train',
                body:
                    'The 16526 Kanyakumari Express leaves Bangalore Cantt at 20:22 and pulls into Varkala Sivagiri at 10:37 the next morning — sleep through the whole journey and wake up 3 km from the beach. The 16315 Kochuveli Express (to Kollam, 16:47 → 07:22) and the KCVL Humsafar from Krishnarajapuram are solid alternates when Kanyakumari Express berths sell out; both leave a short 33.6 km cab from Kollam.',
            },
            {
                id: 'rail-sameday-vandebharat',
                icon: 'train',
                badge: 'Same-day by rail',
                title: 'Morning Vande Bharat to Ernakulam, connect to Varkala',
                duration: '≈ 15 hrs, all rail, same day',
                fareNote: 'CC ₹1,670 + ₹310',
                modesParam: 'Train',
                body:
                    'For an all-rail daylight run: the 26651 Ernakulam Vande Bharat departs KSR Bangalore at 05:10 and arrives 13:50, connecting to the same 16:20 Ernad Express that terminates the day at Varkala Sivagiri by 19:57. RouteChef\u2019s connection buffer here is computed from actual past runs of both trains — a comfortable two-hour-plus margin — so you can judge the transfer risk before booking.',
            },
        ],
        steps: [
            'Book the 07:20 Bangalore (BLR) → Trivandrum (TRV) flight — 1 hour 20 minutes in the air.',
            'Pre-book a cab from Trivandrum airport to Varkala (41.4 km). RouteChef shows hatchback, sedan and SUV fares upfront.',
            'Drive up the coast past Kazhakkoottam and Attingal — about an hour and a quarter.',
            'Reach Varkala cliff before lunch; stays cluster along North Cliff and Odayam beach.',
        ],
        faqs: [
            {
                q: 'Is there a direct train from Bangalore to Varkala?',
                a: 'Yes — the 16526 Kanyakumari Express runs direct from Bangalore Cantt to Varkala Sivagiri (20:22 → 10:37 next morning, about 14 hours), and a weekly SMVB–Trivandrum special also stops at Varkala. Several more trains serve nearby Kollam Jn.',
            },
            {
                q: 'Which is the nearest airport to Varkala from Bangalore?',
                a: 'Trivandrum (TRV), 41.4 km from Varkala, has the shortest onward cab and sub-90-minute flights from Bangalore. Kochi (COK) is the alternative — farther by road, but the Ernad Express from Ernakulam runs directly to Varkala station, eliminating the long cab.',
            },
            {
                q: 'What is the fastest way from Bangalore to Varkala?',
                a: 'The morning flight to Trivandrum plus the 41.4 km cab — about 4 hours door to door, reaching the cliff by lunchtime.',
            },
            {
                q: 'Can I reach Varkala from Bangalore the same day by train?',
                a: 'Yes — take the 05:10 Ernakulam Vande Bharat (arrives 13:50) and connect to the 16:20 Ernad Express, which reaches Varkala Sivagiri at 19:57 the same evening.',
            },
            {
                q: 'What is the cheapest way to reach Varkala from Bangalore?',
                a: 'A sleeper or 3A berth on the direct overnight Kanyakumari Express (3A from about ₹1,180) — no cab needed at either end beyond the 3 km hop from Varkala Sivagiri station to the cliff.',
            },
        ],
    },
};
